import { z } from 'zod';

/*
 * Shapes shared between the admin screens and the API — the email side.
 * reference/data-model.md §8.
 */

export const EMAIL_SUPPRESSION_REASON_LABELS: Record<string, string> = {
  hard_bounce: 'Hard bounce',
  repeated_soft_bounce: 'Repeated soft bounces',
  complaint: 'Marked as spam',
  manual: 'Blocked by hand',
  unsubscribe: 'Unsubscribed',
};

/** Never render a raw enum (CLAUDE.md §4). Falls back to the value rather than to blank. */
export function suppressionReasonLabel(reason: string): string {
  return EMAIL_SUPPRESSION_REASON_LABELS[reason] ?? reason;
}

/**
 * Why each reason happened, in the office's language.
 *
 * On the screen beside the address, because "hard bounce" does not tell whoever
 * is deciding whether to lift it what they are deciding.
 */
export const EMAIL_SUPPRESSION_REASON_HINTS: Record<string, string> = {
  hard_bounce: 'The receiving server said this address does not exist.',
  repeated_soft_bounce: 'Delivery kept failing temporarily — usually a full or broken mailbox.',
  complaint: 'The recipient reported a message as spam.',
  manual: 'Somebody at Honeycomb blocked this address.',
  unsubscribe: 'The recipient asked to stop receiving mail.',
};

export const suppressEmailSchema = z.object({
  email: z.email('That does not look like an email address.'),
  note: z.string().trim().max(512).optional(),
});

export type SuppressEmailInput = z.infer<typeof suppressEmailSchema>;

export const liftSuppressionSchema = z.object({
  email: z.email('That does not look like an email address.'),
});

export type LiftSuppressionInput = z.infer<typeof liftSuppressionSchema>;

export interface SuppressionSummary {
  email: string;
  reason: string;
  detail: string | null;
  softBounceCount: number;
  suppressedAt: string | null;
  createdAt: string;
}
